import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, User, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { X, MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SeoJsonLd from '@/components/SeoJsonLd';

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showWhatsApp, setShowWhatsApp] = useState(false);

  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'PLASA',
    url: 'https://www.plasa.com.mx/',
    telephone: '+52 33 3821 7408',
    email: 'plasapcg@plasa.com.mx',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Carretera La Barca - Atotonilco km 2.5',
      addressLocality: 'La Barca',
      addressRegion: 'Jalisco',
      postalCode: '47910',
      addressCountry: 'MX'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 20.4253066,
      longitude: -102.488366
    },
    openingHours: ['Mo-Fr 09:00-18:00', 'Sa 09:00-14:00']
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Mostrar los datos que se van a enviar
      console.log('Enviando datos:', formData);

      const apiUrl = "/api/send-email";
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      }
      );

      const result = await response.json();

      if (response.ok) {
        // Mostrar mensaje de éxito
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'generate_lead', { event_category: 'contact', value: 1 });
        }
        toast({
          title: t('contact.toast.sentTitle'),
          description: t('contact.toast.sentDescription'),
        });
      } else {
        console.error('Error del servidor:', result.error);
        toast({
          title: t('contact.toast.errorTitle'),
          description: result.message || t('contact.toast.submitErrorDescription'),
          variant: "destructive",
        });
      }

      // Limpiar el formulario
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        message: "",
      });

    } catch (error) {
      console.error('Error al enviar el formulario:', error);
      toast({
        title: t('contact.toast.errorTitle'),
        description: t('contact.toast.successButNoResponse'),
        variant: "default",
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <div className="min-h-screen bg-gray-50 relative">
      <SeoJsonLd data={localBusinessSchema} />
      {/* Botón flotante de WhatsApp */}
      {!showWhatsApp && (
        <motion.button
          onClick={() => setShowWhatsApp(true)}
          className="fixed bottom-8 right-8 bg-green-500 text-white p-4 rounded-full shadow-lg z-50 hover:bg-green-600 transition-colors"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          aria-label={t('contact.whatsapp.openChatLabel')}
        >
          <MessageCircle size={32} />
        </motion.button>
      )}

      {/* Ventana flotante de WhatsApp */}
      {showWhatsApp && (
        <motion.div
          className="fixed bottom-8 right-8 w-80 bg-white rounded-lg shadow-xl z-50 overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
        >
          {/* Encabezado */}
          <div className="bg-green-500 text-white p-4 flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <MessageCircle className="text-white" />
              <span className="font-semibold">{t('contact.whatsapp.chatTitle')}</span>
            </div>
            <button
              onClick={() => setShowWhatsApp(false)}
              className="text-white hover:text-gray-200 transition-colors"
              aria-label={t('contact.whatsapp.closeChatLabel')}
            >
              <X size={20} />
            </button>
          </div>

          {/* Cuerpo del chat */}
          <div className="p-4 bg-gray-50 h-64 overflow-y-auto">
            <div className="mb-4">
              <div className="bg-green-100 text-gray-800 rounded-lg p-3 max-w-[80%] inline-block">
                <p className="text-sm">{t('contact.whatsapp.greeting')}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="bg-blue-500 text-white rounded-lg p-3 max-w-[80%] inline-block">
                <p className="text-sm">{t('contact.whatsapp.userQueryExample')}</p>
              </div>
            </div>
          </div>

          {/* Pie con botón de WhatsApp */}
          <div className="p-4 border-t">
            <a
              href={`https://wa.me/523338217408?text=${encodeURIComponent(t('contact.whatsapp.prefilledMessage'))}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded flex items-center justify-center space-x-2 transition-colors"
            >
              <MessageCircle size={18} />
              <span>{t('contact.whatsapp.openWhatsappBtn')}</span>
            </a>
          </div>
        </motion.div>
      )}

      {/* Header */}
      <section className="relative text-white py-20 overflow-hidden">
        {/* Fondo con patrón */}
        <div className="absolute inset-0 w-full h-full">
          <div className="absolute inset-0 hero-pattern"></div>
          {/* Capa semitransparente para mejorar contraste */}
          <div className="absolute inset-0 bg-black/20"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-5xl font-bold mb-4">{t('contact.hero.title')}</h1>
            <p className="text-xl opacity-90 max-w-2xl mx-auto">
              {t('contact.hero.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col"
          >
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('contact.info.title')}</h2>
              <p className="text-gray-600 text-lg mb-8">
                {t('contact.info.description')}
              </p>
            </div>

            {/* Contact Details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Teléfono */}
              <div className="flex items-start space-x-4 bg-gray-50 p-4 rounded-lg">
                <div className="plasa-red-bg p-3 rounded-lg flex-shrink-0">
                  <Phone className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">{t('contact.info.phoneTitle')}</h3>
                  <p className="text-gray-600 text-sm">(33) 3821 7408</p>
                  <p className="text-gray-600 text-sm">(33) 1871 5068</p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start space-x-4 bg-gray-50 p-4 rounded-lg">
                <div className="plasa-red-bg p-3 rounded-lg flex-shrink-0">
                  <Mail className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">{t('contact.info.emailTitle')}</h3>
                  <a href="mailto:plasapcg@plasa.com.mx" className="text-gray-600 hover:text-plasa-red text-sm">
                    plasapcg@plasa.com.mx
                  </a>
                </div>
              </div>

              {/* Dirección */}
              <div className="flex items-start space-x-4 bg-gray-50 p-4 rounded-lg">
                <div className="plasa-red-bg p-3 rounded-lg flex-shrink-0">
                  <MapPin className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">{t('contact.info.addressTitle')}</h3>
                  <p className="text-gray-600 text-sm">
                    {t('contact.info.addressLine1')}<br />
                    {t('contact.info.addressLine2')}<br />
                    {t('contact.info.addressLine3')}
                  </p>
                </div>
              </div>

              {/* Horarios */}
              <div className="flex items-start space-x-4 bg-gray-50 p-4 rounded-lg">
                <div className="plasa-red-bg p-3 rounded-lg flex-shrink-0">
                  <Clock className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">{t('contact.info.hoursTitle')}</h3>
                  <p className="text-gray-600 text-sm">
                    {t('contact.info.hoursMonFri')}<br />
                    {t('contact.info.hoursSat')}<br />
                    {t('contact.info.hoursSun')}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-auto">
              {/* Mapa de Google Maps */}
              <div className="w-full">
                <div className="w-full h-96 rounded-lg overflow-hidden shadow-lg">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3738.9702217345894!2d-102.488366!3d20.4253066!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x842ec7131d76fd2d%3A0xd1f5841fb4c9aa20!2sPLASA!5e0!3m2!1ses-419!2smx!4v1750272851277!5m2!1ses-419!2smx"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title={t('contact.info.mapTitle')}
                  ></iframe>
                </div>
              </div>

              {/* Dirección debajo */}
              <div className="mt-2 text-gray-600 text-sm text-center">
                {t('contact.info.addressLine1')}<br />
                {t('contact.info.addressLine2')}<br />
                {t('contact.info.addressLine3')}
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg p-8 flex flex-col h-full"
          >
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">{t('contact.form.title')}</h2>
              <p className="text-gray-600">
                {t('contact.form.description')}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.fullNameLabel')}
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200 focus:shadow-lg focus:shadow-red-500/50"
                      placeholder={t('contact.form.fullNamePlaceholder')}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.emailLabel')}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200 focus:shadow-lg focus:shadow-red-500/50"
                      placeholder={t('contact.form.emailPlaceholder')}
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.phoneLabel')}
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200 focus:shadow-lg focus:shadow-red-500/50"
                      placeholder={t('contact.form.phonePlaceholder')}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.companyLabel')}
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200 focus:shadow-lg focus:shadow-red-500/50"
                    placeholder={t('contact.form.companyPlaceholder')}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('contact.form.messageLabel')}
                </label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 text-gray-400 h-5 w-5" />
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200 focus:shadow-lg focus:shadow-red-500/50 resize-none"
                    placeholder={t('contact.form.messagePlaceholder')}
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full plasa-red-bg hover:bg-red-700 py-3 text-lg font-semibold"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    {t('contact.form.sending')}
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <Send className="mr-2 h-5 w-5" />
                    {t('contact.form.submitButton')}
                  </div>
                )}
              </Button>

              <div className="mt-8 p-8 text-center bg-white rounded-xl border-2 border-white shadow-sm">
                <div className="relative inline-flex items-center justify-center mb-6">
                  <div className="absolute inset-0 bg-red-100 rounded-full opacity-75 animate-ping"></div>
                  <div className="relative flex items-center justify-center w-20 h-20 bg-white rounded-full border-4 border-white shadow-md">
                    <svg className="h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-3">{t('contact.form.priorityTitle')}</h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  {t('contact.form.priorityDescription')}
                </p>
              </div>
            </form>
          </motion.div>
        </div>
      </div>

      {/* CTA Section */}
      <section className="py-16 text-white relative overflow-hidden">
        {/* Fondo con patrón */}
        <div className="absolute inset-0 w-full h-full">
          <div className="absolute inset-0 hero-pattern"></div>
          {/* Capa semitransparente para mejorar contraste */}
          <div className="absolute inset-0 bg-black/20"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold mb-4">{t('contact.cta.title')}</h2>
            <p className="text-xl mb-6 opacity-90">
              {t('contact.cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:3338217408"
                className="modern-button group inline-flex items-center justify-center relative overflow-hidden pr-12"
              >
                <span className="whitespace-nowrap">(33) 3821 7408</span>
                <span className="absolute right-3 opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <Phone className="h-5 w-5 animate-bounce-x" />
                </span>
              </a>
              <a
                href="tel:3318715068"
                className="modern-button group inline-flex items-center justify-center relative overflow-hidden pr-12"
              >
                <span className="whitespace-nowrap">(33) 1871 5068</span>
                <span className="absolute right-3 opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <Phone className="h-5 w-5 animate-bounce-x" />
                </span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
